import { useEffect, useState } from "react";
import api from "../lib/api";

/* Auto-fetch a matching Unsplash hero when a trip has no cover photo.
   Renders the image + mandated Unsplash attribution overlay. Silent-fails
   to a soft gradient placeholder if Unsplash is unconfigured or empty. */
export default function TripAutoHero({ query, className = "", alt = "", testid = "trip-auto-hero" }) {
  const [photo, setPhoto] = useState(null);
  const [tried, setTried] = useState(false);

  useEffect(() => {
    if (!query || tried) return;
    setTried(true);
    // Cache by query in sessionStorage so we don't re-hit Unsplash on rerenders.
    const cacheKey = `tp_hero_${query.toLowerCase().trim()}`;
    try {
      const c = sessionStorage.getItem(cacheKey);
      if (c) { setPhoto(JSON.parse(c)); return; }
    } catch { /* noop */ }
    api.get(`/unsplash/search`, { params: { q: query, per_page: 1 } })
      .then((r) => {
        const p = (r.data?.results || [])[0];
        if (p) {
          setPhoto(p);
          try { sessionStorage.setItem(cacheKey, JSON.stringify(p)); } catch { /* noop */ }
          // Fire the mandated Unsplash download-tracking ping (best effort).
          api.post("/unsplash/track-download", { download_location: p.download_location }).catch(() => {});
        }
      })
      .catch(() => { /* no unsplash key or 502 — just render the placeholder */ });
  }, [query, tried]);

  if (!photo) {
    return (
      <div data-testid={`${testid}-placeholder`}
        className={`relative bg-gradient-to-br from-blue-400 via-cyan-500 to-emerald-500 ${className}`}>
        <div className="absolute inset-0 grid place-items-center text-white/80 text-xs font-mono">
          {query || "Trip"}
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`} data-testid={testid}>
      <img src={photo.url_regular} alt={alt || photo.alt || query}
        className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      {/* Unsplash attribution — mandatory per their license.
          Uses onClick to open in a new tab so the credit works even when this
          hero is wrapped in an ancestor <Link> (no nested <a> in <a>). */}
      <div className="absolute bottom-1 left-1 right-1 text-[9px] leading-tight text-white/90 font-500 drop-shadow"
        data-testid={`${testid}-credit`}>
        Photo by{" "}
        <span role="link" tabIndex={0}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(photo.photographer_url, "_blank", "noopener,noreferrer"); }}
          className="underline decoration-white/60 hover:decoration-white cursor-pointer">
          {photo.photographer}
        </span>{" "}on{" "}
        <span role="link" tabIndex={0}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(photo.unsplash_url, "_blank", "noopener,noreferrer"); }}
          className="underline decoration-white/60 hover:decoration-white cursor-pointer">
          Unsplash
        </span>
      </div>
    </div>
  );
}
